import { useState } from "react";
import { toast } from "sonner";
import { http } from "../../lib/api";
import { can, useAuth } from "../../lib/auth";
import { Btn } from "../kit/Kit";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { mxn, sel } from "./common";
import { CamposPanel, ProductoSelector } from "../campos/CamposPanel";
import { QuickLog } from "../actividad/QuickLog";

const SIG = { prospecto: "propuesta", propuesta: "aprobacion", aprobacion: "expediente", expediente: "contratado" };
const err = (e) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Revisa los datos");

const Aprobacion = ({ op, onDone }) => {
  const { user } = useAuth();
  const [c, setC] = useState("");
  const go = (decision) => http.post(`/captacion/pipeline/${op.id}/aprobar`, { decision, comentario: c }).then((r) => { toast.success("Registrado"); onDone(r.data); }).catch(err);
  if (!op.fuera_tabla) return <p className="text-xs text-emerald-700" data-testid="op-en-tabla">Tasa en tabla{op.tabla && ` (v${op.tabla.version})`}: no requiere aprobación.</p>;
  return (
    <div className="rounded-lg bg-amber-50 p-2 text-xs" data-testid="op-aprobacion">
      <p><b>Fuera de tabla</b> {op.tasa_tabla == null ? "(sin tabla vigente)" : `(${op.diff_pp > 0 ? "+" : ""}${op.diff_pp?.toFixed(2)} pp vs ${op.tasa_tabla}%)`} · requiere: {op.aprob_requeridas.join(" + ")} · estado: <b data-testid="op-aprob-estado">{op.aprob_estado || "sin enviar"}</b></p>
      {op.aprobaciones.map((a) => <p key={a.paso}>{a.paso}: {a.decision} por {a.por}{a.suplente ? " (suplente)" : ""} · {a.comentario}</p>)}
      {can(user, "captacion.aprobar_tasa") && op.aprob_estado === "pendiente" && <div className="mt-2 flex flex-wrap gap-2">
        <input value={c} onChange={(e) => setC(e.target.value)} placeholder="Comentario" className={`${sel} flex-1`} data-testid="op-aprob-comentario" />
        <Btn onClick={() => go("aprobar")} data-testid="op-aprobar">Aprobar</Btn><Btn kind="secondary" onClick={() => go("rechazar")} data-testid="op-rechazar">Rechazar</Btn></div>}
    </div>
  );
};

const Docs = ({ op, onDone }) => {
  const subir = (key, f) => { const fd = new FormData(); fd.append("file", f); http.post(`/captacion/pipeline/${op.id}/docs/${key}`, fd).then((r) => { toast.success("Documento cargado"); onDone(r.data); }).catch(err); };
  return (
    <div className="space-y-1 text-xs" data-testid="op-docs">
      {op.docs_requeridos.map((d) => <label key={d.key} className="flex items-center justify-between gap-2 border-t border-[#F2F0EC] py-1">
        <span className={d.ok ? "text-emerald-700" : ""}>{d.ok ? "Cargado · " : ""}{d.label}</span>
        <input type="file" className="max-w-[150px] text-[11px]" onChange={(e) => e.target.files[0] && subir(d.key, e.target.files[0])} data-testid={`op-doc-${d.key}`} /></label>)}
    </div>
  );
};

const Pld = ({ op, onDone }) => {
  const [f, setF] = useState({ estado: op.pld?.estado || "pendiente", nota: "", listas: false });
  const guardar = () => http.put(`/captacion/pipeline/${op.id}/pld`, f).then((r) => { toast.success("PLD actualizado"); onDone(r.data); }).catch(err);
  return (
    <div className="space-y-2 text-xs" data-testid="op-pld">
      <p>Dictamen PLD: <b data-testid="op-pld-estado">{op.pld?.estado}</b>{op.pld?.por && ` · ${op.pld.por}`}</p>
      <div className="flex flex-wrap items-center gap-2">
        <select value={f.estado} onChange={(e) => setF({ ...f, estado: e.target.value })} className={`${sel} w-auto`} data-testid="op-pld-select">{["pendiente", "en_revision", "aprobado", "rechazado"].map((x) => <option key={x}>{x}</option>)}</select>
        <label className="flex items-center gap-1"><input type="checkbox" checked={f.listas} onChange={(e) => setF({ ...f, listas: e.target.checked })} data-testid="op-pld-listas" />Listas consultadas</label>
        <input value={f.nota} onChange={(e) => setF({ ...f, nota: e.target.value })} placeholder="Sustento" className={`${sel} flex-1`} data-testid="op-pld-nota" />
        <Btn kind="secondary" onClick={guardar} data-testid="op-pld-guardar">Guardar</Btn></div>
    </div>
  );
};

export const OpDetalle = ({ op, onClose, onChange }) => {
  const [motivo, setMotivo] = useState("");
  if (!op) return null;
  const mover = (etapa) => http.post(`/captacion/pipeline/${op.id}/etapa`, { etapa, motivo }).then((r) => { toast.success(`Etapa: ${etapa}`); onChange(r.data); }).catch(err);
  const cerrada = ["contratado", "perdido"].includes(op.etapa);
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="op-detalle">
        <SheetHeader><SheetTitle>{op.nombre}</SheetTitle></SheetHeader>
        <div className="mt-3 space-y-4 text-sm">
          <p data-testid="op-resumen">{op.tipo} · {mxn(op.monto)} · {op.plazo || "plazo por definir"} · tasa {op.tasa ?? "—"}% · instrucción {op.instruccion} · etapa <b>{op.etapa}</b> · vía {op.via}</p>
          <Aprobacion op={op} onDone={onChange} />
          <div><p className="mb-1 font-semibold">Expediente del ahorrador</p><Docs op={op} onDone={onChange} /></div>
          <ProductoSelector tipo="prospecto" id={op.id} valor={op.producto_id} onSaved={(pid) => onChange({ ...op, producto_id: pid })} />
          <CamposPanel entidad="prospecto" refId={op.id} />
          <Pld op={op} onDone={onChange} />
          <div className="border-t border-[#F2F0EC] pt-3" data-testid="op-actividad"><p className="mb-2 font-semibold">Registrar actividad</p><QuickLog entidad="prospecto" refId={op.id} prefix="op-ql" /></div>
          {!cerrada && <div className="flex flex-wrap gap-2 border-t border-[#F2F0EC] pt-3">
            {SIG[op.etapa] && <Btn onClick={() => mover(SIG[op.etapa])} data-testid="op-avanzar">Avanzar a {SIG[op.etapa]}</Btn>}
            <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo (pérdida)" className={`${sel} flex-1`} data-testid="op-motivo" />
            <Btn kind="ghost" onClick={() => mover("perdido")} data-testid="op-perdido">Marcar perdida</Btn></div>}
          {op.nota_contratacion && <p className="text-xs text-[#5C5850]">{op.nota_contratacion}</p>}
          <div className="text-[11px] text-[#8C867A]" data-testid="op-historial">{op.historial.slice().reverse().map((h, i) => <p key={i}>{h.fecha.slice(0, 16)} · {h.accion} · {h.por}</p>)}</div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
